import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isUiModificationsSupportEnabled } from '@helpcenter/feature-flags';
import { getActivationId, getCloudId } from '@helpcenter/meta';
import { di } from 'react-magnetic-di';
import type { State } from 'state';
import type { RestUiModificationsData } from 'state/actions/ui-modifications';
import { getUiModificationsData, getUiModificationsError } from 'state/selectors/ui-modifications';
import { getAnalyticsWebClientPromise } from '@atlassian/help-center-common-util/analytics/client';
import { AnalyticsListener } from '@atlassian/ui-modifications-analytics/analytics-listener';
import { fireErrorAnalytics } from '@atlassian/ui-modifications-analytics/utils/fire-error-analytics';
import { UiModificationsRootWithData } from '@atlassian/ui-modifications-core/root/root-with-uim-data';
import type { UiModificationsData as PlatformUiModificationsData } from '@atlassian/ui-modifications-core/root/types';
import { REQUEST_CREATE_VIEW_TYPE } from '../../common/constants';
import { DEFAULT } from '../../common/utils/configuration/constants';
import { ForgeAppRenderer } from './forge-app-renderer';
import type { EntryPointInternalProps, UiModificationsEntryPointProps } from './types';

/**
 * Entry point that reads the fetched UIM payload (per-app configuration and installed
 * extensions) from the `persisted.uiModifications` Redux slice, filled by the JSM models REST
 * epic, and hands it to the platform root. Redux only caches that payload; UIM runtime state
 * lives in the core's react-sweet-state store.
 */
const EntryPointWithReduxData = ({
	portalId,
	requestTypeId,
	triggerPointKey,
	uiModifications,
	uiModificationsError,
}: EntryPointInternalProps) => {
	di(
		UiModificationsRootWithData,
		getActivationId,
		getCloudId,
		ForgeAppRenderer,
		fireErrorAnalytics,
	);

	const executionContextKey = portalId && requestTypeId ? `${portalId}-${requestTypeId}` : null;
	const cloudId = getCloudId();
	const activationId = getActivationId();

	// Fire analytics when there's an error fetching UI modifications
	useEffect(() => {
		if (uiModificationsError) {
			fireErrorAnalytics({
				error: new Error(uiModificationsError),
				id: 'fetchUiModificationsFailure',
				viewType: REQUEST_CREATE_VIEW_TYPE,
				attributes: {
					portalId,
					requestTypeId,
				},
			});
		}
	}, [uiModificationsError, portalId, requestTypeId]);

	const executionContext = useMemo(() => {
		if (!portalId || !requestTypeId) {
			return null;
		}

		return {
			portal: { id: portalId },
			request: { typeId: requestTypeId },
		};
	}, [portalId, requestTypeId]);

	// Wrap Redux data in { jira: ... } structure expected by platform component
	const wrappedUiModificationsData = useMemo((): PlatformUiModificationsData => {
		if (!uiModifications) {
			return { jira: { forge: { uiModificationsExtensions: [] }, uiModifications: [] } };
		}
		return { jira: uiModifications } as PlatformUiModificationsData;
	}, [uiModifications]);

	const triggerPointKeyVal = triggerPointKey ?? DEFAULT;

	return (
		<UiModificationsRootWithData
			executionContext={executionContext}
			uiModificationsData={wrappedUiModificationsData}
			cloudId={cloudId}
			activationId={activationId}
			executionContextKey={executionContextKey}
			triggerPointKey={triggerPointKeyVal}
			ForgeAppRenderer={ForgeAppRenderer}
		/>
	);
};

const selector = createSelector(
	(state: State, props: UiModificationsEntryPointProps) => {
		const { portalId, requestTypeId } = props;

		let uiModifications: RestUiModificationsData | undefined;
		let uiModificationsError: string | undefined;

		if (portalId && requestTypeId) {
			uiModifications = getUiModificationsData(state, portalId, requestTypeId);
			uiModificationsError = getUiModificationsError(state, portalId, requestTypeId);
		}

		return {
			portalId,
			requestTypeId,
			uiModifications,
			uiModificationsError,
			triggerPointKey: props.triggerPointKey,
		};
	},
	(selectorData) => selectorData,
);

const EntryPoint = connect(selector)(EntryPointWithReduxData);

export const UiModificationsEntryPoint = (props: UiModificationsEntryPointProps) => {
	di(
		AnalyticsListener,
		isUiModificationsSupportEnabled,
		ErrorBoundary,
		getAnalyticsWebClientPromise,
	);

	return isUiModificationsSupportEnabled() ? (
		<ErrorBoundary id="uiModifications" packageName="jsmUiModificationsViewRequestCreate">
			<AnalyticsListener
				analyticsWebClient={getAnalyticsWebClientPromise().then((client) => client.getInstance())}
			>
				<EntryPoint {...props} />
			</AnalyticsListener>
		</ErrorBoundary>
	) : null;
};
